import {produce} from 'immer';
import invert from 'lodash/invert';

import {reporter} from 'common/report/error-reporting';
import {
  FlatSegment,
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsSeriesTree,
  MetricMetadata,
  ReportSectionsMetadata,
} from 'common/report/insights/util/types';
import {
  _getDefaultInsightsSorting,
  getFlatSegmentDataTableColumns,
  getFlatSegments,
  getFlatSegmentTableRow,
  getPivotTableSegmentMetadata,
  getSegmentLookupMap,
  getSeriesDisplaySegmentColumns,
  getSortConfig,
  groupEventComparisonShowMetadata,
} from 'common/report/insights/util';
import {ChartType, RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {
  ColumnGroup,
  Columns,
  CommonDataTableFields,
  SegmentColumnSpec,
  SegmentMetadataNode,
  SortTransformFunc,
  TableColumn,
  TableColumnType,
  TableDataType,
  TableRow,
  TimeComparisonColumnType,
} from 'common/components/data-table/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {FlatValueSortConfig, SortBy, SortConfig} from 'common/report/util/sorting/types';
import {createRowCompareFunc} from 'common/report/util/sorting/table-sorting';
import {commaizeNumber, formatPercent, getAlphaLabelFromIndex, getOwnAttr, MeasureUtil} from 'common/util';
import {getLiftBaselineSegment} from 'common/report/util/lift';
import DataTableStylusConstants from 'common/components/data-table/stylus-constants.json';
import {
  filterSortedRowsWithViewN,
  flattenColumns,
  getUnlimitedViewNSortConfig,
  isPostViewNSortConfig,
  segmentCellBlocksEnabled,
} from 'common/components/data-table/utils';
import {LiftType} from 'common/report/util/lift/types';
import {getSelectedAggregationData} from 'common/query-builder/query-entry-section/util';
import {getEntries} from 'common/report/insights/util/events';
import {
  isFormulaEntry,
  isMetricEntry,
  MetricEntry,
  QueryMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {getSelectedPropertyLabel} from 'common/query-builder/query-block/properties-menu/util';
import {renamePropertyValue} from 'common/report/util/properties';
import {getEventSelectedLabel} from 'common/query-builder/query-block/events-menu/util';
import {SortByValueConfig} from 'common/report/util/sorting/types';
import {AttributionTypeToSpec} from 'common/widgets/multi-attribution-menu/constants';
import {getSelectedCountBy, getSelectedCountByLabel} from 'common/query-builder/query-block/count-menu/util';
import {MetricType} from 'common/query-builder/query-block/constants';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getMultiAttributionFromEntry} from 'common/query-builder/query-builder/util';
import {AbsoluteDateRange} from 'common/util/date';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaEntry} from 'common/util/formulas';

import {OverallResultsMap, pivotSeries} from './pivot';

export {pivotSeries};

export type InsightsTableColumnContext = {
  metricKey?: string;
  isTimeComparison?: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTableData({
  results,
  bookmarkParams,
  dataGroups,
  pivotResults,
  canUseViewN,
  metricResource,
}: {
  results: InsightsQueryResults;
  bookmarkParams: InsightsBookmarkParams;
  dataGroups: Record<string, DataGroup>;
  pivotResults: boolean;
  canUseViewN?: boolean;
  metricResource: MetricsResource;
}) {
  let metrics: Record<string, string> = null;
  let overallResults: OverallResultsMap = null;
  let timeComparisonOverallResults: OverallResultsMap = null;
  let liftComparisonOverallResults: OverallResultsMap = null;

  if (pivotResults) {
    const metaClauses = results.meta.report_sections.show || results.meta.report_sections.formula;
    const pivotedResults = pivotSeries(
      metaClauses,
      results.series,
      results.meta.report_sections.show ? bookmarkParams.sections.show : null,
    );
    overallResults = pivotedResults.overallResults;
    metrics = pivotedResults.metrics;

    results = produce(results, (resultsDraft) => {
      resultsDraft.series = pivotedResults.series;
      // the first header in the results are always the metric header, for a pivot table
      // view, we want the first header to be the first segment
      resultsDraft.headers.shift();
      if (resultsDraft.time_comparison) {
        const timeComparisonPivotedResults = pivotSeries(
          metaClauses,
          results.time_comparison.series,
          results.meta.report_sections.show ? bookmarkParams.sections.show : null,
        );
        resultsDraft.time_comparison.series = timeComparisonPivotedResults.series;
        timeComparisonOverallResults = timeComparisonPivotedResults.overallResults;
      }
      if (resultsDraft.lift_comparison) {
        const liftComparisonPivotedResults = pivotSeries(
          metaClauses,
          results.lift_comparison.series,
          results.meta.report_sections.show ? bookmarkParams.sections.show : null,
        );
        resultsDraft.lift_comparison.series = liftComparisonPivotedResults.series;
        liftComparisonOverallResults = liftComparisonPivotedResults.overallResults;
      }
    });
  }

  /** sorting configs for a pivot table differ from any persisted bookmark table sorting state  */
  let sortConfig = getSortConfig({
    bookmarkSortConfig: bookmarkParams.sorting,
    headerCount: results.headers.length,
    // we intentionally do not pass hasPivotTablesEnabled
    // since pivotSeries removes the `$event` header
    canUseViewN,
  })[ChartType.Table];
  let defaultSortConfig = _getDefaultInsightsSorting({numSegmentColumns: results.headers.length, canUseViewN})[
    ChartType.Table
  ];

  if (pivotResults) {
    sortConfig = updateSortingForPivotView(sortConfig);
    /** @todo pablo - remove once pivot tables is GA and this becomes the default sorting path */
    defaultSortConfig = updateSortingForPivotView(defaultSortConfig);
  }
  if (canUseViewN && !isPostViewNSortConfig(sortConfig)) {
    const hasPartialViewNSortConfig =
      (sortConfig.sortBy === SortBy.Value && sortConfig.viewNLimit !== undefined) ||
      sortConfig.colSortAttrs.some((colSortAttr) => colSortAttr.viewNLimit !== undefined);
    if (hasPartialViewNSortConfig) {
      reporter.warn(`getTableData: found unexpected viewNLimit in sortConfig`, {sortConfig});
    }

    // Transform SortConfig to have viewNLimit for unlimited segments. This carries over existing behavior of showing
    // all segments, while adding View N state so it can be used by mp-data-table.
    sortConfig = getUnlimitedViewNSortConfig(sortConfig);
    defaultSortConfig = getUnlimitedViewNSortConfig(defaultSortConfig);
  }

  // displays lift overall comparisons when available
  const hasLiftComparison = Boolean(results.lift_comparison?.series);

  // for lift mode comparisons, results also include a time comparison series, we need to differentiate
  // when to display results for that time comparison series with multiple columns for each metric (current & past)
  // and when to display lift comparison values, these two modes are mutually exclusive
  const hasTimeComparison = Boolean(results.time_comparison?.series) && hasLiftComparison === false;

  // for the regular table view, time comparisons become the main value in the data table
  const series = pivotResults ? results.series : results.lift_comparison?.series || results.series;

  const segments = getFlatSegments(results.headers, series);
  const segmentLookup = getSegmentLookupMap(segments);
  const segmentColumns = getSeriesDisplaySegmentColumns(bookmarkParams.sections, results, dataGroups, {
    includeMetricHeader: !pivotResults,
  });

  // both timeComparisonLookup and liftComparisonLookup define maps of all segment paths in
  // their corresponding result time series to a FlagSegment entry, much like we define
  // segments computed above, these maps are later used to influence how sorting works and
  // metadata displayed on each row (lift percentages and comparison values)
  const timeComparisonLookup = generateComparisonLookupMap(results.time_comparison?.series, results.headers);

  const liftComparisonLookup = generateComparisonLookupMap(results.lift_comparison?.series, results.headers);

  let columnGroups: Columns<any, InsightsTableColumnContext>;
  if (pivotResults) {
    columnGroups = getPivotedTableColumns({
      headers: results.headers,
      segments,
      bookmarkParams,
      sortConfig,
      segmentColumns,
      metrics,
      hasTimeComparison,
      hasLiftComparison,
      overallResults,
      timeComparisonOverallResults,
      liftComparisonOverallResults,
      reportSectionsMetadata: results.meta.report_sections,
      dataGroups,
      canUseViewN,
      timeComparisonMetadata: {
        currentDateRange: {
          fromDate: results.date_range?.from_date,
          toDate: results.date_range?.to_date,
        },
        pastDateRange: {
          fromDate: results.time_comparison?.date_range.from_date,
          toDate: results.time_comparison?.date_range.to_date,
        },
      },
      metricResource,
    });
  } else {
    const renderer = hasLiftComparison
      ? ({item, context}) => formatPercent(item[context.field] ?? 0)
      : ({item, context}) => commaizeNumber(item[context.field] ?? 0);
    columnGroups = getFlatSegmentDataTableColumns({segmentColumns, hasTimeComparison, cellRenderer: renderer});
  }

  // when displaying rows in the table, we don't want to display the $overall segment values
  // these are rendered as auxiliary information on each segment category
  const segmentsWithoutOveralls = segments.filter((segment) => segment.path.at(-1) !== RESULTS_OVERALL_SUM_KEY);
  let segmentMetadata: SegmentMetadataNode;
  let rows: TableRow[];
  if (pivotResults) {
    rows = segmentsWithoutOveralls.map((segment) =>
      getPivotedTableRow({
        segment,
        metrics,
        timeComparisonSegment: timeComparisonLookup?.get(segment.key),
        liftComparisonSegment: liftComparisonLookup?.get(segment.key),
        liftBaseline: bookmarkParams.liftComparison
          ? getLiftBaselineSegment(
              bookmarkParams.liftComparison,
              segment.path,
              segmentLookup,
              timeComparisonLookup?.get(segment.key),
            )
          : null,
      }),
    );

    if (segmentCellBlocksEnabled({columns: flattenColumns(columnGroups), sorting: sortConfig})) {
      const isTimeComparisonLift = bookmarkParams.liftComparison?.type === LiftType.Time;
      const isOverallComparison = bookmarkParams.liftComparison?.type === LiftType.Overall;
      segmentMetadata = getPivotTableSegmentMetadata({
        series: results.series,
        liftType: bookmarkParams.liftComparison?.type,
        ...(isTimeComparisonLift && {
          comparisonSeries: results.time_comparison?.series,
          liftSeries: results.lift_comparison?.series,
        }),
        ...(isOverallComparison && {
          liftSeries: results.lift_comparison?.series,
        }),
        metrics,
      });
    }
  } else {
    rows = segmentsWithoutOveralls.map((segment) =>
      getFlatSegmentTableRow({segment, timeComparisonSegment: timeComparisonLookup?.get(segment.key)}),
    );
  }

  sortConfig = sanitizeTableSort(sortConfig, columnGroups);
  defaultSortConfig = sanitizeTableSort(defaultSortConfig, columnGroups);

  const compareFunc = createRowCompareFunc({sortConfig, columns: columnGroups, rows});
  rows = rows.sort(compareFunc);
  if (canUseViewN) {
    rows = filterSortedRowsWithViewN({rows, columns: columnGroups, sortConfig});
  }

  return {rows, columnGroups, defaultSortConfig, sortConfig, segmentMetadata};
}

function generateComparisonLookupMap(series: InsightsSeriesTree, headers: string[]): Map<string, FlatSegment> | null {
  if (Boolean(series) === false) {
    return null;
  }

  const timeComparisonSegments = getFlatSegments(headers, series);
  return getSegmentLookupMap(timeComparisonSegments);
}

/**
 * Returns the column definition for all segments and metrics (as columns)
 * in a pivoted series result
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function getPivotedTableColumns({
  headers,
  segments,
  bookmarkParams,
  sortConfig,
  segmentColumns,
  metrics,
  hasTimeComparison,
  hasLiftComparison,
  overallResults,
  timeComparisonOverallResults,
  liftComparisonOverallResults,
  reportSectionsMetadata,
  dataGroups,
  canUseViewN,
  timeComparisonMetadata,
  metricResource,
}: {
  headers?: string[];
  segments: FlatSegment[];
  bookmarkParams: InsightsBookmarkParams;
  sortConfig: SortConfig;
  segmentColumns: SegmentColumnSpec[];
  metrics: Record<string, string>;
  hasTimeComparison: boolean;
  hasLiftComparison: boolean;
  overallResults: OverallResultsMap;
  timeComparisonOverallResults?: OverallResultsMap;
  liftComparisonOverallResults?: OverallResultsMap;
  reportSectionsMetadata: ReportSectionsMetadata;
  dataGroups: Record<string, DataGroup>;
  canUseViewN: boolean;
  timeComparisonMetadata?: {
    currentDateRange: AbsoluteDateRange;
    pastDateRange: AbsoluteDateRange;
  };
  metricResource: MetricsResource;
}): Columns<any, InsightsTableColumnContext> {
  const hasOverallComparison = bookmarkParams.liftComparison?.type === LiftType.Overall;
  // when doing lift comparisons, we want sorting to be based on
  // $overall results from the lift comparison series results
  // with the exception for overall lift comparison since $overall
  // results are always 1. (@todo change the API to compute the lift on overall as well)
  const overallResultsForSort =
    hasLiftComparison && hasOverallComparison === false ? liftComparisonOverallResults : overallResults;

  const segmentSortTransform = createOverallSortTransform(overallResultsForSort, hasLiftComparison);

  const defaultWidths = getInitialSegmentColumnWidths({segments, columns: segmentColumns, canUseViewN});

  const columns: Columns<any, InsightsTableColumnContext> = segmentColumns.map(({title, segmentType}, index) => {
    const useColValueSorting =
      sortConfig.sortBy === SortBy.Column && sortConfig.colSortAttrs[index].sortBy === SortBy.Value;
    return {
      field: `${CommonDataTableFields.SegmentPrefix}${index}`,
      type: TableColumnType.Segment,
      contentType: segmentType,
      title,
      sticky: true,
      hasCheckbox: false,
      isSortable: false,
      labelTransform: (row, field) => renamePropertyValue(row[field], headers[index]),
      initialWidth: defaultWidths[index],
      fixedWidth: true,
      resizable: true,
      isSelectable: true,
      sortTransform: useColValueSorting ? segmentSortTransform : null,
    };
  });

  let queryMetricEntries: Array<QueryMetricEntry | MetricEntry>;
  let metaClauses: Array<{
    /** refers to the index in the QB selection */
    metricIndex: number;
    /** refers to the show clause metadata key */
    metricKey: string;
    metadata: MetricMetadata;
    isSubQuery?: boolean;
  }>;

  if (reportSectionsMetadata.show) {
    // metaClauses = reportSectionsMetadata.show;
    queryMetricEntries = getEntries({
      showClauses: bookmarkParams.sections.show,
      dataGroups: Object.values(dataGroups),
      dateUnit: bookmarkParams.sections.time[0].unit,
      reportSectionsMetadata,
    });
    // handle event comparisons metadata by grouping them together
    const groupedShowMetadata = groupEventComparisonShowMetadata(reportSectionsMetadata.show);
    metaClauses = groupedShowMetadata
      .flatMap((clauseMetadata, index) => {
        const entry = queryMetricEntries[index];
        if (Array.isArray(clauseMetadata)) {
          return clauseMetadata.map((clause) => ({
            ...clause,
            metricKey: clause.metric_key,
            metricIndex: index,
          }));
        }
        if (entry.isHidden) {
          return null;
        }

        if (clauseMetadata.sub_queries) {
          return clauseMetadata.sub_queries.map((subClause) => ({
            metadata: clauseMetadata,
            metricIndex: index,
            metricKey: subClause.metric_key,
            isSubQuery: true,
          }));
        }

        return {
          metadata: clauseMetadata,
          metricIndex: index,
          metricKey: clauseMetadata.metric_key,
        };
      })
      .filter(Boolean);
  } else {
    metaClauses = reportSectionsMetadata.formula.map((clauseMetadata, index) => ({
      metadata: clauseMetadata,
      metricKey: clauseMetadata.metric_key,
      metricIndex: index,
    }));
  }

  const metricNameToMetricId = invert(metrics);
  const dataGroupList = Object.values(dataGroups);

  for (const [tableMetricIndex, metaClause] of metaClauses.entries()) {
    let title: string;
    let subtitle: string;
    const metricId = metricNameToMetricId[metaClause.metricKey];
    const metricOverall = overallResults.get(`$event`)[metricId];
    const timeComparisonOverall = getOwnAttr(timeComparisonOverallResults?.get(`$event`), metricId);

    if (queryMetricEntries) {
      const queryMetricEntry = queryMetricEntries[metaClause.metricIndex];
      if (metaClause.isSubQuery && !isMetricEntry(queryMetricEntry) && !isFormulaEntry(queryMetricEntry)) {
        queryMetricEntry.event.label = metaClause.metricKey; // replaces $top_events label name with sub-query metric
      }

      if (isFormulaEntry(queryMetricEntry)) {
        const resolvedFormulaEntry = getResolvedFormulaEntry({
          formulaEntry: queryMetricEntry,
          metricsResource: metricResource,
        });
        title = resolvedFormulaEntry?.event.name || `Loading...`;
        subtitle = `Formula`;
      } else {
        // TODO: title for metric entry should be the name of the metric
        title = isMetricEntry(queryMetricEntry)
          ? queryMetricEntry.behavior.name || metaClause.metricKey
          : getEventSelectedLabel(queryMetricEntry.event);
        const dataGroupId = isMetricEntry(queryMetricEntry)
          ? queryMetricEntry.behavior.dataGroupId
          : queryMetricEntry.dataGroupId;
        if (FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProjectStore.value)) {
          const countBy = getSelectedCountBy(queryMetricEntry);
          subtitle = getSelectedCountByLabel({
            selected: countBy,
            metricType: MetricType.Event,
            dataGroupId,
            timePeriod: ``,
            dataGroups: dataGroupList,
            property: countBy.property,
          });
        } else if (isMetricEntry(queryMetricEntry)) {
          // TODO: Remove this condition once insights new count menu is rolled out
          // We don't want metric entry to work in old insights conut menu
          // This is just a placeholder to avoid lint error
          subtitle = ``;
        } else {
          const {firstPassAggregationLabel, secondPassAggregationLabel} = getSelectedAggregationData({
            entry: queryMetricEntry,
            dataGroupId,
            dataGroups: dataGroupList,
            forNewWorkflow: true,
          });
          const subtitleParts = [
            ...(queryMetricEntry.property ? [getSelectedPropertyLabel({selected: queryMetricEntry.property})] : []),
            firstPassAggregationLabel,
            ...(secondPassAggregationLabel ? [secondPassAggregationLabel] : []),
          ];
          subtitle = subtitleParts.join(` - `);
        }
      }
      if (getMultiAttributionFromEntry(queryMetricEntry)) {
        subtitle = `${subtitle} • ${AttributionTypeToSpec[getMultiAttributionFromEntry(queryMetricEntry).type].label}`;
      }
    } else {
      const formulaClause = bookmarkParams.sections.formula[metaClause.metricIndex];
      title = formulaClause.label ?? `Formula ${metaClause.metricIndex + 1}`;
      subtitle = formulaClause.value;
    }

    let sortSelectTitle = `${getAlphaLabelFromIndex(metaClause.metricIndex)} - ${title}`;
    if (subtitle) {
      sortSelectTitle += ` [${subtitle}]`;
    }

    const columnContext: InsightsTableColumnContext = {
      metricKey: metaClause.metricKey,
    };

    // For time comparisons we render a column for Current and Past
    if (hasTimeComparison && timeComparisonMetadata) {
      columns.push(
        {
          sortSelectTitle,
          context: columnContext,
          field: getMetricFieldName(tableMetricIndex),
          type: TableColumnType.Data,
          contentType: TableDataType.Number,
          title,
          titlePrefix: getAlphaLabelFromIndex(metaClause.metricIndex),
          subtitle,
          isSortable: false,
          sticky: false,
          resizable: true,
          isSelectable: true,
          metricOverall,
          timeComparisonMetadata: {
            dateRange: timeComparisonMetadata.currentDateRange,
            timeComparisonColumnType: TimeComparisonColumnType.Current,
          },
        },
        {
          sortSelectTitle,
          context: {...columnContext, isTimeComparison: true},
          field: getMetricFieldName(tableMetricIndex, `compare`),
          type: TableColumnType.Data,
          contentType: TableDataType.Number,
          isSortable: false,
          sticky: false,
          resizable: true,
          isSelectable: true,
          metricOverall: timeComparisonOverall,
          timeComparisonMetadata: {
            dateRange: timeComparisonMetadata.pastDateRange,
            timeComparisonColumnType: TimeComparisonColumnType.Past,
          },
        },
      );
    } else {
      /** results without time comparisons */
      const isTimeComparisonLift = bookmarkParams.liftComparison?.type === LiftType.Time;
      columns.push({
        title,
        subtitle,
        sortSelectTitle,
        context: columnContext,
        field: getMetricFieldName(tableMetricIndex),
        type: TableColumnType.Data,
        contentType: TableDataType.Number,
        titlePrefix: getAlphaLabelFromIndex(metaClause.metricIndex),
        isSortable: false,
        sticky: false,
        resizable: true,
        isSelectable: true,
        metricOverall,
        ...(isTimeComparisonLift && {
          liftOverall: liftComparisonOverallResults?.get(`$event`)[metricId],
          comparisonOverall: timeComparisonOverall,
        }),
        liftField: getMetricFieldName(tableMetricIndex, `lift`),
        liftExtra: getMetricFieldName(tableMetricIndex, `compare`),
        // when lift mode is activated, rows sort by the percentage over baseline value
        ...(hasLiftComparison
          ? {sortTransform: (row) => row[getMetricFieldName(tableMetricIndex, `lift`)] ?? Number.MIN_SAFE_INTEGER}
          : {}),
      });
    }
  }

  return columns;
}

/**
 * For a given segment path, computes the different
 * metric value fields to be rendered for each row
 *
 * @see {@link pivotSeries} for reference on the shape of a segment value
 * on a pivoted time series
 *
 * @returns TableRow
 */
function getPivotedTableRow({
  segment,
  metrics,
  timeComparisonSegment,
  liftComparisonSegment,
  liftBaseline,
}: {
  segment: FlatSegment;
  metrics: Record<string, string>;
  timeComparisonSegment?: FlatSegment;
  liftComparisonSegment?: FlatSegment;
  liftBaseline?: FlatSegment;
}): TableRow {
  const row = {
    identifier: segment.key,
  };

  for (const [index, path] of segment.path.entries()) {
    row[`${CommonDataTableFields.SegmentPrefix}${index}`] = path;
  }

  // defines all metric values for a particular segment path
  for (const [index, metricId] of Object.keys(metrics).entries()) {
    row[getMetricFieldName(index)] = segment.value[metricId];
    row[getMetricFieldName(index, `compare`)] = liftBaseline?.value[metricId] ?? timeComparisonSegment?.value[metricId];
    row[getMetricFieldName(index, `lift`)] = liftComparisonSegment?.value[metricId];
  }

  return row;
}

/**
 * For pivot tables, we switch from using `totalValue` as the
 * main field for value columns (since this is not limited to one) and
 * move to using `$mp-metric-0` as the identifier for data columns
 *
 * @param sortConfig
 * @returns SortConfig
 */
export function updateSortingForPivotView(sortConfig: SortConfig) {
  const defaultValue = getMetricFieldName(0);

  return produce(sortConfig, (draft) => {
    // attempts to match each column sort config to a valid multi metric sort configuration
    for (const colSort of draft.colSortAttrs) {
      const valueSortField = (colSort as SortByValueConfig).valueField;
      const hasInvalidField = valueSortField
        ? valueSortField.startsWith(CommonDataTableFields.MetricPrefix) === false
        : true;

      (colSort as SortByValueConfig).valueField = hasInvalidField ? defaultValue : valueSortField;
    }

    if (draft.valueField === CommonDataTableFields.Total) {
      draft.valueField = defaultValue;
    }
  });
}

/**
 * Defines the common field for multi metric tables
 * note that this value must be independent of the metric name
 *
 * @param index
 * @param additionalPrefix
 * @returns string
 */
function getMetricFieldName(index: number, additionalSuffix: string = null) {
  additionalSuffix = additionalSuffix ? `-${additionalSuffix}` : ``;
  return CommonDataTableFields.MetricPrefix + `${index}${additionalSuffix}`;
}

/**
 * When doing column value sorting, we will sort based on the `$overall` value at each segment
 * level as opposed to aggregated sum trees which is the default behavior of `createRowCompareFunc`
 *
 * @param overallResults - overall results for all segmentation levels to based sorting on
 *
 * @see OverallResultsMap
 * @see createRowCompareFunc
 */
function createOverallSortTransform(overallResults: OverallResultsMap, hasLiftComparison = false): SortTransformFunc {
  return (row, _segmentField, {path, colSortConfig}) => {
    let value = undefined;
    const metricId = (colSortConfig as FlatValueSortConfig).valueField;
    const overallValue = overallResults.get(path);

    if (overallValue) {
      /**
       * For all segmentation paths, non leaf results will define a $overall result which
       * represents the unsegmented metric value for that path, when sorting table rows
       * we use that metric value instead of performing tree sums (default table behavior)
       *
       * Example results:
       *
       * US / $overall = 15
       * US / Firefox = 10
       * US / Chrome = 5
       * ---
       * Spain / $overall = 20
       * Spain / Chrome = 19
       * Spain / Firefox = 1
       *
       * For the segmentation path `US`, in a descending sort order, `Spain` will come first
       * all other leaf results (US / Chrome) will sort based on the metric value for that path
       */
      value = overallValue[metricId];
    } else if (hasLiftComparison) {
      // when doing lift comparisons, sort leaf results based on the lift percentage values
      value = row[`${metricId}-lift`];
    } else {
      // sorts leaf values based on the absolute metric value
      value = row[metricId];
    }

    return value ?? Number.MIN_SAFE_INTEGER;
  };
}

/**
 * Makes sure all selected sort fields are valid for a given table configuration
 */
function sanitizeTableSort(sortConfig: SortConfig, columns: Array<TableColumn | ColumnGroup>) {
  const defaultValue = getMetricFieldName(0);

  const getFirstColumn = (column: TableColumn | ColumnGroup): TableColumn => {
    if (`columns` in column) {
      return column.columns[0];
    } else {
      return column;
    }
  };

  const findColumn = (field) => columns.find((column) => getFirstColumn(column).field === field) ?? null;

  if (sortConfig.sortBy === SortBy.Column) {
    for (const colSort of sortConfig.colSortAttrs) {
      if (colSort.sortBy === SortBy.Value && findColumn(colSort.valueField) === null) {
        colSort.valueField = defaultValue;
      }
    }
  } else if (sortConfig.valueField && findColumn(sortConfig.valueField) === null) {
    sortConfig.valueField = defaultValue;
  }

  return sortConfig;
}

/**
 * Computes a similar sizing logic to `minmax(DEFAULT_SEGMENT_COLUMN_WIDTH, fit-content)` in a CSS grid layout
 * for all segment columns in the table
 *
 * @returns array of column widths
 */
function getInitialSegmentColumnWidths({
  segments,
  columns,
  canUseViewN,
}: {
  segments: FlatSegment[];
  columns: SegmentColumnSpec[];
  canUseViewN: boolean;
}): number[] {
  const DEFAULT_SEGMENT_COLUMN_WIDTH = 250;
  // allocates the text width + double the horizontal padding of cells for better readability
  const measure = (label) =>
    MeasureUtil.measureTextWidth(label, `500 14px ${mpFonts.fontFamilyPrimary}`) +
    DataTableStylusConstants.CELL_HORIZONTAL_PADDING * 4;

  // populate initial widths with column title lengths
  let widths = Array.from({length: columns.length}, (_value, index) =>
    Math.min(DEFAULT_SEGMENT_COLUMN_WIDTH, measure(columns[index].title)),
  );

  for (const segment of segments) {
    for (const [index, pathValue] of segment.path.entries()) {
      if (pathValue.length === 0) {
        continue;
      }

      const computedWidth = Math.min(DEFAULT_SEGMENT_COLUMN_WIDTH, measure(pathValue));
      widths[index] = Math.max(widths[index], computedWidth);
    }
  }

  if (canUseViewN) {
    // TODO(mack): Move this function into mp-data-table so it can be used for other tables besides Insights pivot
    // table. At that point, can use computed View N label rather than hardcoding a min width as I've done for now.
    // Set min width to account for the View N label
    widths = widths.map((width) => Math.max(width, 120));
  }

  return widths;
}
